import { Badge, Button, Drawer, formatDate, useToast } from "@helmr/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { newIdempotencyKey } from "../../api/client";
import {
  bookMember, cancelBooking, cancelOccurrence, completeOccurrence, listOccurrences, markBooking, memberPlans, waiveBooking,
  type BookingEntitlementInput, type ClassBooking, type ClassOccurrence, type MemberHit,
} from "../../api/classes";
import { useServices } from "../../app/services";
import k from "./classes.module.css";
import { Confirm, Failure, FormError, messageOf, RowsSkeleton, styles as c, useScope } from "./common";
import { MemberPicker } from "./MemberPicker";

const STATUS: Record<string, { label: string; tone: "ok" | "warn" | "crit" | "info" | "neutral" }> = {
  booked: { label: "Booked", tone: "info" }, waitlisted: { label: "Waitlist", tone: "warn" }, attended: { label: "Attended", tone: "ok" },
  no_show: { label: "No-show", tone: "crit" }, late_cancel: { label: "Late cancel", tone: "crit" }, cancelled: { label: "Cancelled", tone: "neutral" },
};

export function OccurrenceDrawer({ occurrence, trainerName, onClose }: { occurrence: ClassOccurrence | null; trainerName: string; onClose: () => void }) {
  const { client } = useServices();
  const sc = useScope();
  const qc = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [booking, setBooking] = useState(false);
  // One lever per action (D57), as each POST /classes/* route checks.
  const canAdd = sc.ctx.has("classes.book");
  const canComplete = sc.ctx.has("classes.complete_occurrence");
  const canCancelClass = sc.ctx.has("classes.cancel_occurrence");
  const canMark = sc.ctx.has("classes.attendance");
  const canCancelBooking = sc.ctx.has("classes.cancel_booking");
  const canWaive = sc.ctx.has("classes.waive");
  const id = occurrence?.id ?? null;

  const q = useQuery({
    queryKey: ["classes", "occurrence", ...sc.key, id],
    queryFn: () => listOccurrences(client, { from: occurrence!.date, to: occurrence!.date, templateId: occurrence!.templateId, bookings: true }).then((r) => r.occurrences.find((o) => o.id === id) ?? null),
    enabled: id !== null, retry: false,
  });
  const live = q.data ?? occurrence;
  const refresh = () => qc.invalidateQueries({ queryKey: ["classes"] });
  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => { setError(null); void refresh(); },
    onError: (e) => setError(messageOf(e)),
  });
  const run = (fn: () => Promise<unknown>, done?: string) => act.mutate(fn, { onSuccess: () => done && toast.show(done) });

  const bookings = live?.bookings ?? [];
  const total = live ? `${live.seatsTaken ?? 0}/${live.capacity}` : "";
  const full = !!live && (live.seatsLeft ?? 1) === 0;
  const open = id !== null;

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => { if (!o) { setBooking(false); setError(null); onClose(); } }}
      title={live?.name ?? "Class"}
      description={live ? `${formatDate(live.date, sc.ctx.locale, "long")} · ${live.start}–${live.end} · ${trainerName}` : undefined}
      footer={live && live.status === "scheduled" && (canAdd || canComplete || canCancelClass) ? (
        <div className={c.row}>
          {canAdd ? <Button variant="primary" onClick={() => setBooking((b) => !b)}>{booking ? "Hide booking" : "Book a member"}</Button> : null}
          {canComplete && !live.taughtAt ? <Button busy={act.isPending} busyLabel="Working…" onClick={() => run(() => completeOccurrence(client, live.id, false), "Class marked complete")}>Mark class complete</Button> : null}
          {canCancelClass ? <Confirm label="Cancel class" question="Cancel this class? Everyone booked is released and credits go back." busy={act.isPending} onConfirm={() => run(() => cancelOccurrence(client, live.id), "Class cancelled")} /> : null}
        </div>
      ) : undefined}
    >
      {!live ? null : (
        <div className={`${c.stack} ${c.drawerPad}`}>
          <div className={c.row}>
            <Badge tone={full ? "warn" : "neutral"}>{total}</Badge>
            {full ? <Badge tone="warn">Full</Badge> : null}
            {(live.waitlisted ?? 0) > 0 ? <Badge tone="warn">{live.waitlisted} on waitlist</Badge> : null}
            {live.status === "cancelled" ? <Badge tone="neutral">Cancelled</Badge> : null}
            {live.taughtAt ? <Badge tone="ok">Taught</Badge> : null}
          </div>
          <FormError message={error} />
          {booking ? <BookMemberForm occurrence={live} onBooked={(msg) => { toast.show(msg); setBooking(false); void refresh(); }} /> : null}
          {q.isPending && bookings.length === 0 ? <RowsSkeleton rows={3} /> : q.error ? <Failure error={q.error} title="Could not load bookings" onRetry={() => void q.refetch()} /> : bookings.length === 0 ? (
            <p className={c.note}>No bookings yet.</p>
          ) : (
            <ul aria-label="Bookings">{bookings.map((b) => (
              <BookingRow key={b.id} b={b} busy={act.isPending} canMark={canMark && live.status === "scheduled"} canCancel={canCancelBooking && live.status === "scheduled"} canWaive={canWaive}
                onMark={(s) => run(() => markBooking(client, b.id, s), s === "attended" ? "Marked attended" : "Marked no-show")}
                onCancel={() => run(() => cancelBooking(client, b.id), "Booking cancelled")}
                onWaive={() => run(() => waiveBooking(client, b.id), "Charge waived")} />
            ))}</ul>
          )}
          {bookings.some((b) => b.refundDue) ? <p className={c.note}>Refunds are due on this class. <Link to="/billing?tab=refunds">Open Billing → Refunds</Link></p> : null}
        </div>
      )}
    </Drawer>
  );
}

function BookingRow({ b, canMark, canCancel, canWaive, busy, onMark, onCancel, onWaive }: { b: ClassBooking; canMark: boolean; canCancel: boolean; canWaive: boolean; busy: boolean; onMark: (s: "attended" | "no_show") => void; onCancel: () => void; onWaive: () => void }) {
  const st = STATUS[b.status] ?? { label: b.status, tone: "neutral" as const };
  const name = b.member?.name ?? "Member";
  return (
    <li className={k.bk}>
      <div className={c.trunc}>
        <strong className={c.trunc} title={name}>{name}</strong>
        <span className={c.note}>{b.member?.phone ?? ""}{b.entitlement.kind ? ` · ${b.entitlement.kind.replace("_", " ")}` : ""}</span>
      </div>
      <div className={k.acts}>
        <Badge tone={st.tone}>{st.label}</Badge>
        {b.refundDue ? <Badge tone="warn">Refund due</Badge> : null}
        {canMark && b.status === "booked" ? <Button size="sm" disabled={busy} aria-label={`Mark ${name} attended`} onClick={() => onMark("attended")}>Attended</Button> : null}
        {canMark && b.status === "booked" ? <Button size="sm" disabled={busy} aria-label={`Mark ${name} no-show`} onClick={() => onMark("no_show")}>No-show</Button> : null}
        {canCancel && (b.status === "booked" || b.status === "waitlisted") ? <Button size="sm" variant="plain" disabled={busy} aria-label={`Cancel booking for ${name}`} onClick={onCancel}>Cancel booking</Button> : null}
        {canWaive && (b.status === "no_show" || b.status === "late_cancel") ? <Button size="sm" disabled={busy} aria-label={`Waive ${name}`} onClick={onWaive}>Waive</Button> : null}
      </div>
    </li>
  );
}

function BookMemberForm({ occurrence, onBooked }: { occurrence: ClassOccurrence; onBooked: (message: string) => void }) {
  const { client } = useServices();
  const sc = useScope();
  const key = useRef(newIdempotencyKey());
  const [member, setMember] = useState<MemberHit | null>(null);
  const [choice, setChoice] = useState<string>("");
  const [paymentId, setPaymentId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const plans = useQuery({ queryKey: ["classes", "member-plans", ...sc.key, member?.id], queryFn: () => memberPlans(client, member!.id), enabled: member !== null, retry: false });
  const usable = (plans.data ?? []).filter((p) => p.kind === "membership" || p.kind === "class_pack");
  const book = useMutation({
    mutationFn: (entitlement: BookingEntitlementInput) => bookMember(client, occurrence.id, member!.id, entitlement, key.current),
    onSuccess: (r) => { key.current = newIdempotencyKey(); onBooked(r.waitlisted ? `${member!.name} is on the waitlist` : `Booked ${member!.name}`); },
    onError: (e) => setError(messageOf(e)),
  });
  const submit = () => {
    setError(null);
    if (choice === "drop_in") return paymentId.trim() ? book.mutate({ kind: "drop_in", paymentId: paymentId.trim() }) : setError("Enter the payment id for the drop-in.");
    const plan = usable.find((p) => p.id === choice);
    if (!plan) return setError("Choose what this booking uses.");
    book.mutate({ kind: plan.kind as "membership" | "class_pack", planInstanceId: plan.id });
  };
  return (
    <section className={c.stack} aria-label="Book a member">
      <MemberPicker picked={member?.id} onPick={(m) => { setMember(m); setChoice(""); setError(null); }} />
      {member ? (
        <fieldset className={`${c.stack} ${k.plain}`}>
          <legend className={c.note}>Book {member.name} on</legend>
          {plans.isPending ? <RowsSkeleton rows={2} /> : plans.error ? <Failure error={plans.error} title="Could not load plans" onRetry={() => void plans.refetch()} /> : null}
          {usable.map((p) => (
            <label key={p.id} className={c.plan}>
              <input type="radio" name="entitlement" value={p.id} checked={choice === p.id} onChange={() => setChoice(p.id)} />
              <span>{p.kind === "membership" ? "Membership" : "Class pack"} · {p.status}{p.endDate ? ` · to ${p.endDate}` : ""}</span>
            </label>
          ))}
          <label className={c.plan}>
            <input type="radio" name="entitlement" value="drop_in" checked={choice === "drop_in"} onChange={() => setChoice("drop_in")} /><span>Drop-in (paid)</span>
          </label>
          {choice === "drop_in" ? <input aria-label="Payment id" className={c.input} placeholder="pay_…" value={paymentId} onChange={(e) => setPaymentId(e.target.value)} /> : null}
          <FormError message={error} />
          <div><Button variant="primary" busy={book.isPending} busyLabel="Booking…" onClick={submit}>Book</Button></div>
        </fieldset>
      ) : null}
    </section>
  );
}
